(() => {
  if (customElements.get('nosht-bundle-signals')) return;

  // The installed Quantity Bundles widget documents these events, but no
  // initial-selection getter. The headless kaching-bundle-component API is a
  // different product and must not be assumed on kaching-bundles-block.
  const widgetSelector = 'kaching-bundles-block';
  const eventNames = ['deal-bar-selected', 'variant-selected', 'variants-changed'];
  const stockStates = ['in-stock', 'low-stock', 'sold-out', 'continue-selling', 'preorder', 'unknown'];
  const messageKeys = {
    'in-stock': 'inStock',
    'low-stock': 'lowStock',
    'sold-out': 'unavailable',
    'continue-selling': 'backorder',
    preorder: 'preorder',
    unknown: 'unknown'
  };

  function variantId(value) {
    if (typeof value === 'number' && !Number.isSafeInteger(value)) return null;
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    const id = String(value);
    return /^[1-9]\d*$/.test(id) ? id : null;
  }

  function positiveQuantity(value) {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    if (typeof value === 'string' && !/^[1-9]\d*$/.test(value)) return null;
    const quantity = Number(value);
    return Number.isSafeInteger(quantity) && quantity > 0 ? quantity : null;
  }

  function assessSelection(rows, variants) {
    const unknown = { state: 'unknown', deliveryText: '' };
    if (!Array.isArray(rows) || rows.length === 0) return unknown;
    const quantities = new Map();
    for (const row of rows) {
      const id = variantId(row?.variantId);
      const quantity = positiveQuantity(row?.quantity);
      if (!id || quantity === null) return unknown;
      const combined = (quantities.get(id) || 0) + quantity;
      if (!Number.isSafeInteger(combined)) return unknown;
      quantities.set(id, combined);
    }

    let unavailable = false;
    let backorder = false;
    let preorder = false;
    let lowStock = false;
    let deliveryAllowed = true;
    let deliveryText;
    for (const [id, required] of quantities) {
      const stock = variants.get(id);
      if (!stock || typeof stock.available !== 'boolean' || typeof stock.managed !== 'boolean') return unknown;
      if (!stock.available) {
        unavailable = true;
        deliveryAllowed = false;
        continue;
      }
      // Available without tracked stock means orderable, not proven in stock.
      if (!stock.managed || !Number.isSafeInteger(stock.quantity) || typeof stock.continueSelling !== 'boolean') return unknown;
      if (stock.preorder === true) {
        preorder = true;
        deliveryAllowed = false;
      }
      if (stock.quantity < required) {
        if (stock.continueSelling) backorder = true;
        else unavailable = true;
        deliveryAllowed = false;
      } else {
        const threshold = stock.lowStockThreshold;
        if (!Number.isSafeInteger(threshold) || threshold < 1) return unknown;
        if (stock.quantity <= threshold) lowStock = true;
      }
      const text = typeof stock.deliveryText === 'string' ? stock.deliveryText.trim() : '';
      if (stock.deliveryEligible !== true || !text || (deliveryText !== undefined && text !== deliveryText)) deliveryAllowed = false;
      deliveryText = deliveryText === undefined ? text : deliveryText;
    }

    const state = unavailable ? 'sold-out' : preorder ? 'preorder' : backorder ? 'continue-selling' : lowStock ? 'low-stock' : 'in-stock';
    return { state, deliveryText: deliveryAllowed ? deliveryText || '' : '' };
  }

  function deliveryDuration(text) {
    const pattern = /\d+(?:\s*[–—-]\s*\d+)?\s+(?:business\s+days?|working\s+days?|(?:arki)?päiv(?:ää|än|ässä|ä)|arbetsdag(?:ar)?|vardag(?:ar)?|dag(?:ar)?|days?|Tage[n]?|días?|jours?|giorni)(?![\p{L}\p{N}_])/giu;
    let duration = null;
    for (const match of text.matchAll(pattern)) {
      const before = text.slice(0, match.index);
      // Do not emphasize a suffix of a decimal, date, time or signed value.
      if (/[\p{L}\p{N}_]$/u.test(before) || /\d[\s.,/:–—-]*$/u.test(before) || /[+−–—-]\s*$/u.test(before)) continue;
      // Multiple durations in merchant copy are ambiguous; preserve plain text.
      if (duration) return null;
      duration = { index: match.index, text: match[0] };
    }
    return duration;
  }

  class NoshtBundleSignals extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._generation = (this._generation || 0) + 1;
      this._expected = this.dataset.bundleExpected === 'true';
      this._hasSeenWidget = false;
      this._selection = null;
      this._widget = null;
      this._seenEvents = new WeakSet();
      this._variants = new Map();
      this._messages = {};
      this._showInventory = true;
      this._inventoryDisplayMode = 'all';
      try {
        const data = JSON.parse(this.querySelector('[data-nosht-bundle-stock]')?.textContent || '{}');
        this._messages = data.messages || {};
        this._showInventory = data.showInventory !== false;
        this._inventoryDisplayMode = data.inventoryDisplayMode === 'low_only' ? 'low_only' : 'all';
        const stocks = Array.isArray(data.variants) ? data.variants : [];
        for (const stock of stocks) {
          const id = variantId(stock?.id);
          if (!id || this._variants.has(id)) throw new Error('Invalid stock data');
          this._variants.set(id, stock);
        }
      } catch (_error) {
        this._variants.clear();
      }

      this._inventory = this.querySelector('[data-nosht-bundle-inventory]');
      this._inventoryText = this.querySelector('[data-nosht-bundle-inventory-text]');
      this._delivery = this.querySelector('[data-nosht-bundle-delivery]');
      this._deliveryText = this.querySelector('[data-nosht-bundle-delivery-text]');
      this._nativeRows = Array.from(this.querySelectorAll('[data-nosht-bundle-native], [data-nosht-bundle-incoming]'));
      this._nativeHidden = this._nativeRows.map((row) => row.hidden);
      this._deliveryHidden = this._delivery?.hidden;
      this._nativeDeliveryText = this._deliveryText?.textContent;
      this._formattedDeliveryText = undefined;
      this._setDeliveryText(this._nativeDeliveryText);
      this._scope = this.closest('.section--main-product');
      this._onEvent = this._onWidgetEvent.bind(this);

      if (this._scope) {
        // Capture catches a documented event emitted immediately after a widget
        // is inserted, before MutationObserver can attach its direct listeners.
        eventNames.forEach((name) => this._scope.addEventListener(name, this._onEvent, true));
        this._observer = new MutationObserver(() => this._scheduleReconcile());
        this._observer.observe(this._scope, { childList: true, subtree: true });
      }
      this._reconcile();
    }

    disconnectedCallback() {
      this._connected = false;
      this._generation = (this._generation || 0) + 1;
      this._observer?.disconnect();
      this._observer = null;
      if (this._scope) eventNames.forEach((name) => this._scope.removeEventListener(name, this._onEvent, true));
      this._detachWidget();
      this._restoreNative();
      this._scope = null;
      this._selection = null;
      this._reconcilePending = false;
    }

    _scheduleReconcile() {
      if (!this._connected || this._reconcilePending) return;
      this._reconcilePending = true;
      const generation = this._generation;
      queueMicrotask(() => {
        if (!this._connected || generation !== this._generation) return;
        this._reconcilePending = false;
        this._reconcile();
      });
    }

    _detachWidget() {
      if (this._widget) eventNames.forEach((name) => this._widget.removeEventListener(name, this._onEvent));
      this._widget = null;
    }

    _reconcile() {
      if (!this._connected) return;
      const widgets = this._scope ? Array.from(this._scope.querySelectorAll(widgetSelector))
        .filter((widget) => widget.closest('.section--main-product') === this._scope) : [];
      if (widgets.length > 0) this._hasSeenWidget = true;
      const widget = widgets.length === 1 ? widgets[0] : null;
      if (widget !== this._widget) {
        this._detachWidget();
        this._selection = null;
        this._widget = widget;
        if (widget) eventNames.forEach((name) => widget.addEventListener(name, this._onEvent));
      }
      // Never reuse another widget's or an earlier render's selected items.
      this._render();
    }

    _onWidgetEvent(event) {
      if (!this._connected || this._seenEvents.has(event) || !event.target?.matches?.(widgetSelector)) return;
      this._reconcile();
      if (event.target !== this._widget) return;
      this._seenEvents.add(event);
      // Partial deal/variant events invalidate any earlier complete selection.
      this._selection = event.type === 'variants-changed'
        ? assessSelection(event.detail?.variantIdQuantities, this._variants)
        : null;
      this._render();
    }

    _render() {
      const guarded = this._expected || this._hasSeenWidget;
      this.dataset.bundleGuardActive = String(guarded);
      if (!guarded) {
        this._restoreNative();
        delete this.dataset.bundleStockState;
        return;
      }
      const result = this._selection || { state: 'unknown', deliveryText: '' };
      this.dataset.bundleStockState = result.state;
      this._nativeRows.forEach((row) => { row.hidden = true; });
      if (this._inventory) {
        const message = this._messages[messageKeys[result.state]];
        this._inventory.hidden = !this._showInventory ||
          (this._inventoryDisplayMode === 'low_only' && result.state !== 'low-stock') ||
          typeof message !== 'string' || !message.trim();
        stockStates.forEach((state) => this._inventory.classList.toggle(`nosht-purchase-signal--${state}`, state === result.state));
        if (this._inventoryText && typeof message === 'string' && this._inventoryText.textContent !== message) this._inventoryText.textContent = message;
      }
      if (this._delivery) this._delivery.hidden = !result.deliveryText;
      if (result.deliveryText) this._setDeliveryText(result.deliveryText);
    }

    _setDeliveryText(text) {
      if (!this._deliveryText || typeof text !== 'string') return;
      // The section observer also sees our child nodes. An unchanged message
      // must not create more mutations on every reconcile/restore cycle.
      if (this._formattedDeliveryText === text && this._deliveryText.textContent === text) return;
      const duration = deliveryDuration(text);
      if (duration) {
        const emphasis = document.createElement('strong');
        emphasis.className = 'nosht-purchase-signal__duration';
        emphasis.textContent = duration.text;
        this._deliveryText.replaceChildren(
          document.createTextNode(text.slice(0, duration.index)),
          emphasis,
          document.createTextNode(text.slice(duration.index + duration.text.length))
        );
      } else if (this._deliveryText.textContent !== text || this._deliveryText.children.length) {
        this._deliveryText.textContent = text;
      }
      this._formattedDeliveryText = text;
    }

    _restoreNative() {
      if (this._inventory) this._inventory.hidden = true;
      this._nativeRows?.forEach((row, index) => { row.hidden = this._nativeHidden[index]; });
      if (this._delivery) this._delivery.hidden = this._deliveryHidden;
      this._setDeliveryText(this._nativeDeliveryText);
    }
  }

  customElements.define('nosht-bundle-signals', NoshtBundleSignals);
})();
